import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, CreditCard, Sparkles } from 'lucide-react';
import PillarLayout from '../pillar/PillarLayout';
import CheckoutModal from '../CheckoutModal';

const CANONICAL = 'https://enbet-consulting.de/ki-kombi-paket';

const bullets = [
  { t: 'KI Ready Check inklusive', s: 'Quantifizierte Verlustzeiten, herstellerneutrale Diagnose, datenbasierter Audit-Report (PDF).' },
  { t: 'KI Strategie Workshop inklusive', s: 'Konkrete 3-Phasen-Roadmap mit ROI-Kalkulation, Use-Case-Priorisierung und Quick-Wins.' },
  { t: '1.450 € günstiger als einzeln', s: 'Statt 850 € + 3.500 € = 4.350 € zahlst du im Kombi 2.900 € netto. Sofortzahlung.' },
  { t: 'Direkter Weg von Diagnose zu Plan', s: 'Beide Module aufeinander abgestimmt: Strategie-Workshop nutzt direkt die Ergebnisse des Ready Checks.' },
  { t: 'Festpreis, keine Überraschungen', s: 'Klare Leistung, klarer Preis. Kein Tagessatz-Wuchern, kein versteckter Aufwand.' },
];

const faqItems = [
  {
    q: 'Warum ist das Kombi-Paket günstiger?',
    a: 'Weil Sofortzahlung uns Planungssicherheit gibt und wir die Module direkt nacheinander abwickeln können. Statt 4.350 € einzeln zahlst du 2.900 € netto — du sparst 1.450 €. Voraussetzung: das Kombi muss bei der Buchung in einer Zahlung beglichen werden.',
  },
  {
    q: 'Was bekomme ich konkret?',
    a: 'Beide Module komplett: Modul 1 (KI Ready Check, 1-2 Wochen) liefert deinen datenbasierten Audit-Report. Direkt im Anschluss Modul 2 (Strategie-Workshop), wo wir aus den Befunden eine konkrete Roadmap bauen — mit ROI-Rechnung, Use-Case-Priorisierung und 3-Phasen-Plan.',
  },
  {
    q: 'Wie lange dauert das insgesamt?',
    a: 'Plane 3-5 Wochen ein: ca. 1-2 Wochen für den Ready Check (inklusive Auswertung), dann etwa eine Woche Vorbereitung Workshop, dann der Workshop selbst (1 Tag) plus Nachbereitung 3-5 Tage.',
  },
  {
    q: 'Was passiert wenn ich nach Modul 1 nicht weitermachen will?',
    a: 'Das Kombi-Paket bei Sofortzahlung ist ein verbindlicher Auftrag für beide Module. Wenn du dir noch unsicher bist, ob Modul 2 für euch passt, buche zuerst Modul 1 einzeln (850 €) und entscheide danach. Mit dem Bundle-Rabatt verlierst du dann allerdings.',
  },
  {
    q: 'Alle Preise netto?',
    a: 'Ja, alle Preise verstehen sich netto. Auf 2.900 € kommen 19 % MwSt drauf (= 3.451 € brutto).',
  },
];

export default function LandingKombi() {
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const schemaLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': `${CANONICAL}#service`,
        name: 'KI Ready Check + Strategie Workshop (Kombi-Paket)',
        provider: { '@id': 'https://enbet-consulting.de/#organization' },
        serviceType: 'KI-Bestandsaufnahme und Strategie-Roadmap im Bundle',
        description: 'Modul 1 (Ready Check, 850 €) + Modul 2 (Strategie-Workshop, 3.500 €) im Kombi-Paket. Bei Sofortzahlung 2.900 € statt 4.350 € — du sparst 1.450 € netto.',
        offers: {
          '@type': 'Offer',
          price: '2900',
          priceCurrency: 'EUR',
          availability: 'https://schema.org/InStock',
          url: CANONICAL,
        },
        areaServed: 'DE',
        inLanguage: 'de-DE',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Start', item: 'https://enbet-consulting.de/' },
          { '@type': 'ListItem', position: 2, name: 'Kombi-Paket Modul 1 + 2', item: CANONICAL },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <PillarLayout
      title="Kombi-Paket: KI Ready Check + Strategie Workshop — 2.900 € netto | ENBET Consulting"
      description="Modul 1 (Ready Check) + Modul 2 (Strategie-Workshop) im Kombi-Paket. Bei Sofortzahlung 2.900 € statt 4.350 € — du sparst 1.450 € netto."
      canonical={CANONICAL}
      schemaLd={schemaLd}
    >
      {/* Hero */}
      <section className="bg-[#0a0a0a] text-white pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-[#e07a2f] text-xs uppercase tracking-[0.3em] font-bold mb-4 inline-flex items-center gap-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Sparkles size={14} /> Bundle · Sofortzahlung · 1.450 € sparen
          </motion.p>

          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight leading-[1.05] mb-6"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            Kombi-Paket<br />Modul 1 + 2
          </motion.h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="text-lg md:text-xl text-white/70 max-w-2xl mb-8">
            KI Ready Check und KI Strategie Workshop direkt nacheinander. Eine Buchung, ein Festpreis,
            klarer Weg von der ehrlichen Diagnose zur konkreten Roadmap.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
            className="inline-flex flex-col items-start gap-1 border border-[#befa0a]/30 bg-[#befa0a]/5 px-6 py-4 mb-8">
            <div className="inline-flex items-baseline gap-4 flex-wrap">
              <span className="text-[#befa0a] text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>2.900 €</span>
              <span className="text-white/40 line-through text-lg md:text-xl">4.350 €</span>
              <span className="text-white/60 text-sm uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Sofortzahlung · Sie sparen 1.450 €
              </span>
            </div>
            <span className="text-white/40 text-[11px]">netto, zzgl. 19 % MwSt</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4">
            <Link to="/#booking"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white hover:text-[#0a0a0a] transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Check size={16} /> Kostenloses Erstgespräch
            </Link>
            <button type="button" onClick={() => setCheckoutOpen(true)}
              className="inline-flex items-center justify-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <CreditCard size={16} /> Direkt buchen (2.900 €)
            </button>
          </motion.div>
        </div>
      </section>

      {/* Was steckt drin */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <p className="text-[#658a07] text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Was im Paket steckt
          </p>
          <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight leading-[1.1] mb-10 text-[#1f1f1f]"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            Diagnose + Roadmap.<br />Ein Auftrag, ein Festpreis.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {bullets.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }} transition={{ duration: 0.5, delay: i * 0.05 }}
                className="border-l-2 border-[#befa0a] pl-5 py-1">
                <h3 className="font-bold text-[#1f1f1f] mb-1.5" style={{ fontFamily: "'Oswald', sans-serif" }}>{b.t}</h3>
                <p className="text-sm text-[#1f1f1f]/70 leading-relaxed">{b.s}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vergleich Einzeln vs. Bundle */}
      <section className="bg-[#fafafa] py-20 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <p className="text-[#658a07] text-xs uppercase tracking-[0.3em] font-bold mb-3 text-center" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Einzeln vs. Bundle
          </p>
          <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight leading-tight mb-10 text-[#1f1f1f] text-center"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            Was kostet das einzeln, was im Paket?
          </h2>

          <div className="bg-white border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-2 divide-x divide-gray-200">
              <div className="p-6 md:p-8">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Einzeln gebucht
                </p>
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#1f1f1f]/70">Modul 1 — Ready Check</span>
                    <span className="font-bold text-[#1f1f1f]">850 €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#1f1f1f]/70">Modul 2 — Strategie-Workshop</span>
                    <span className="font-bold text-[#1f1f1f]">3.500 €</span>
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between">
                  <span className="font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>Summe</span>
                  <span className="font-bold text-[#1f1f1f] text-lg">4.350 €</span>
                </div>
              </div>
              <div className="p-6 md:p-8 bg-[#0a0a0a] text-white">
                <p className="text-[#befa0a] text-xs uppercase tracking-wider mb-3 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Kombi-Paket · Sofortzahlung
                </p>
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Modul 1 + 2 zusammen</span>
                    <span className="font-bold">2.900 €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#befa0a]">Sie sparen</span>
                    <span className="font-bold text-[#befa0a]">−1.450 €</span>
                  </div>
                </div>
                <div className="border-t border-white/20 pt-3 flex justify-between">
                  <span className="font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Ihr Preis</span>
                  <span className="font-bold text-[#befa0a] text-lg">2.900 €</span>
                </div>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-3 text-center">Alle Preise netto, zzgl. 19 % MwSt.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-[800px] mx-auto px-6 md:px-10 text-center">
          <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-5 text-[#1f1f1f]"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            Bereit für Diagnose + Roadmap?
          </h2>
          <p className="text-[#1f1f1f]/70 mb-8">
            Wer Modul 1 und 2 zusammen bucht, spart nicht nur Geld, sondern auch Zeit.
            Die zwei Module sind aufeinander abgestimmt — keine Brüche, kein Re-Onboarding.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#booking"
              className="inline-flex items-center justify-center gap-2 border border-[#1f1f1f]/30 text-[#1f1f1f] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#1f1f1f] hover:text-white transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <ArrowRight size={16} /> Erstgespräch buchen
            </Link>
            <button type="button" onClick={() => setCheckoutOpen(true)}
              className="inline-flex items-center justify-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#1f1f1f] hover:text-white transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <CreditCard size={16} /> Direkt buchen (2.900 €)
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#fafafa] py-20 md:py-24">
        <div className="max-w-[800px] mx-auto px-6 md:px-10">
          <p className="text-[#658a07] text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Häufige Fragen
          </p>
          <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-10 text-[#1f1f1f]"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            FAQ
          </h2>
          <div className="space-y-6">
            {faqItems.map((f, i) => (
              <div key={i} className="border-l-2 border-[#befa0a] pl-5">
                <h3 className="font-bold text-[#1f1f1f] mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>{f.q}</h3>
                <p className="text-sm text-[#1f1f1f]/70 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        module="kombi-1-2"
        productName="Kombi-Paket Modul 1 + 2"
        amountLabel="2.900 €"
      />
    </PillarLayout>
  );
}
